import { useEffect } from 'react';
import { ExternalLink, Info, Lightbulb, X } from 'lucide-react';

/**
 * Step-by-step "how do I get these credentials?" guides for every integration
 * shown in Settings (global), per-company (SmartBill) and per-user (Calendar,
 * Gmail). Opened from the info button on each IntegrationCard.
 */

interface HelpStep {
  title: string;
  details: string[];
}

interface IntegrationHelp {
  /** Short explanation of what the integration does / what you need. */
  intro: string;
  steps: HelpStep[];
  /** Extra tips / gotchas shown after the steps. */
  notes?: string[];
  links?: { label: string; url: string }[];
}

export const INTEGRATION_HELP: Record<string, IntegrationHelp> = {
  smtp: {
    intro:
      'SMTP este protocolul prin care aplicația trimite email-uri (Daily Summary, invitații, remindere de facturi). Ai nevoie de datele serverului de email al firmei: host, port, utilizator și parolă. Mai jos găsești pașii pentru Gmail/Google Workspace — cel mai des folosit — plus indicații pentru alți furnizori.',
    steps: [
      {
        title: 'Alege contul de email de pe care se vor trimite mesajele',
        details: [
          'Recomandat: o adresă dedicată, de ex. noreply@firma-ta.ro sau office@firma-ta.ro.',
          'Toate email-urile trimise de aplicație vor apărea ca venind de la această adresă.',
        ],
      },
      {
        title: 'Pentru Gmail / Google Workspace: activează verificarea în 2 pași',
        details: [
          'Intră pe myaccount.google.com → „Securitate" → „Verificarea în 2 pași" și activeaz-o.',
          'Fără verificarea în 2 pași, Google nu îți permite să creezi o parolă de aplicație (App Password), iar parola normală a contului NU funcționează pentru SMTP.',
        ],
      },
      {
        title: 'Creează o parolă de aplicație (App Password)',
        details: [
          'Intră pe myaccount.google.com/apppasswords (sau caută „App passwords" în setările contului Google).',
          'Dă-i un nume (ex. „CRM Firmio") și apasă „Create".',
          'Google îți afișează o parolă de 16 caractere (ex. „abcd efgh ijkl mnop"). Copiaz-o imediat — nu o vei mai putea vedea ulterior.',
        ],
      },
      {
        title: 'Completează câmpurile în formular',
        details: [
          'SMTP Host: smtp.gmail.com (pentru Gmail/Workspace).',
          'Port: 587 (STARTTLS, recomandat) sau 465 (SSL — bifează și „Secure").',
          'User: adresa completă de email (ex. noreply@firma-ta.ro).',
          'Password: parola de aplicație de 16 caractere generată la pasul anterior (fără spații).',
          'From address: adresa care apare ca expeditor; de obicei aceeași cu User.',
        ],
      },
      {
        title: 'Alți furnizori de email',
        details: [
          'Outlook / Microsoft 365: host smtp.office365.com, port 587, user = adresa de email, parolă = parola contului sau o parolă de aplicație.',
          'Yahoo: host smtp.mail.yahoo.com, port 465 (Secure bifat), necesită parolă de aplicație.',
          'Hosting propriu (cPanel etc.): datele SMTP se găsesc în panoul de hosting la „Email Accounts" → „Connect Devices" / „Mail Client Configuration".',
        ],
      },
      {
        title: 'Testează conexiunea',
        details: [
          'Apasă „Testează conexiunea" — aplicația trimite un email real de test către adresa From/User.',
          'Dacă testul reușește, apasă „Salvează" pentru a stoca datele criptat în baza de date.',
        ],
      },
    ],
    notes: [
      'Parola de aplicație oferă acces la contul de email — trateaz-o ca pe o parolă normală și nu o distribui.',
      'Dacă primești „Invalid login" pe Gmail, aproape sigur folosești parola contului în loc de parola de aplicație.',
    ],
    links: [
      { label: 'Google — Parole de aplicație', url: 'https://myaccount.google.com/apppasswords' },
      {
        label: 'Google — Ajutor App Passwords',
        url: 'https://support.google.com/accounts/answer/185833',
      },
    ],
  },

  openai: {
    intro:
      'Cheia OpenAI este folosită pentru embeddings (căutarea semantică în Knowledge Base), pentru transcrierea apelurilor și pentru Firmio Voice — asistentul vocal (GPT-Live, ~0,05 USD/minut de conversație). Ai nevoie de un cont OpenAI Platform (diferit de contul ChatGPT obișnuit ca facturare) cu credit activ.',
    steps: [
      {
        title: 'Creează / autentifică-te în contul OpenAI Platform',
        details: [
          'Intră pe platform.openai.com și autentifică-te (sau creează un cont).',
          'Atenție: abonamentul ChatGPT Plus NU include acces API — API-ul se plătește separat, per utilizare.',
        ],
      },
      {
        title: 'Adaugă o metodă de plată / credit',
        details: [
          'Mergi la platform.openai.com → „Settings" → „Billing".',
          'Adaugă un card sau cumpără credit (ex. 5–10 USD sunt suficienți pentru embeddings mult timp — costul e foarte mic).',
          'Fără credit activ, cheia va funcționa la test dar cererile de embeddings vor fi refuzate.',
        ],
      },
      {
        title: 'Generează cheia API',
        details: [
          'Mergi la platform.openai.com/api-keys („API keys" în meniul din stânga).',
          'Apasă „Create new secret key", dă-i un nume (ex. „CRM Firmio").',
          'Copiază cheia imediat (începe cu „sk-…") — este afișată o singură dată.',
        ],
      },
      {
        title: 'Salvează în aplicație',
        details: [
          'Lipește cheia în câmpul „API Key", apasă „Testează conexiunea", apoi „Salvează".',
        ],
      },
    ],
    notes: [
      'Dacă cheia expune eroarea 401, a fost ștearsă/revocată din contul OpenAI — generează una nouă.',
      'Poți vedea consumul în platform.openai.com → „Usage".',
    ],
    links: [
      { label: 'OpenAI — API keys', url: 'https://platform.openai.com/api-keys' },
      {
        label: 'OpenAI — Billing',
        url: 'https://platform.openai.com/settings/organization/billing/overview',
      },
    ],
  },

  anthropic: {
    intro:
      'Cheia Anthropic alimentează asistentul AI (chat) și rezumatele inteligente din KB Sync. Ai nevoie de un cont pe Anthropic Console cu credit activ.',
    steps: [
      {
        title: 'Creează / autentifică-te în Anthropic Console',
        details: [
          'Intră pe console.anthropic.com și autentifică-te (sau creează un cont).',
          'Abonamentul Claude.ai (Pro/Max) NU include acces API — API-ul se plătește separat, per utilizare.',
        ],
      },
      {
        title: 'Adaugă credit',
        details: [
          'Mergi la console.anthropic.com → „Billing" (sau „Plans & Billing").',
          'Adaugă un card sau cumpără credit. Poți seta și limite lunare de cheltuieli.',
        ],
      },
      {
        title: 'Generează cheia API',
        details: [
          'Mergi la console.anthropic.com → „API Keys".',
          'Apasă „Create Key", dă-i un nume (ex. „CRM Firmio") și confirmă.',
          'Copiază cheia imediat (începe cu „sk-ant-…") — este afișată o singură dată.',
        ],
      },
      {
        title: 'Alege modelul pentru KB Sync',
        details: [
          'Sonnet — recomandat: raport calitate/cost foarte bun pentru rezumate.',
          'Opus — cel mai capabil, pentru analize financiare complexe, dar mai scump.',
        ],
      },
      {
        title: 'Salvează în aplicație',
        details: [
          'Lipește cheia în câmpul „API Key", apasă „Testează conexiunea", apoi „Salvează".',
        ],
      },
    ],
    notes: [
      'Consumul AI al aplicației poate fi urmărit și limitat din tab-ul „Buget AI" din Settings.',
      'Dacă testul întoarce 401, cheia e greșită sau revocată — generează una nouă.',
    ],
    links: [
      { label: 'Anthropic Console — API Keys', url: 'https://console.anthropic.com/settings/keys' },
      {
        label: 'Anthropic Console — Billing',
        url: 'https://console.anthropic.com/settings/billing',
      },
    ],
  },

  googleDrive: {
    intro:
      'Aplicația stochează fișierele (documente, contracte, extrase) într-un Drive comun (Shared Drive), folosind un „Service Account" — un cont tehnic Google care nu aparține unei persoane. Ai nevoie de: (1) un fișier JSON cu cheia Service Account-ului și (2) ID-ul Drive-ului comun în care ai adăugat acel cont ca membru. Trebuie neapărat un Drive comun, nu un folder din „My Drive"-ul cuiva: conturile tehnice nu au spațiu de stocare propriu, iar într-un folder personal scrierea eșuează.',
    steps: [
      {
        title: 'Creează un proiect în Google Cloud Console',
        details: [
          'Intră pe console.cloud.google.com și autentifică-te cu contul Google al firmei.',
          'În bara de sus, deschide selectorul de proiecte → „New Project" → dă-i un nume (ex. „CRM Firmio") → „Create".',
          'Asigură-te că proiectul nou creat este selectat.',
        ],
      },
      {
        title: 'Activează Google Drive API',
        details: [
          'Meniu → „APIs & Services" → „Library".',
          'Caută „Google Drive API" → deschide-l → apasă „Enable".',
        ],
      },
      {
        title: 'Creează Service Account-ul',
        details: [
          'Meniu → „IAM & Admin" → „Service Accounts" → „Create Service Account".',
          'Nume: ex. „crm-drive". Poți sări peste pașii opționali de roluri/acces → „Done".',
          'Notează adresa de email generată (ex. crm-drive@proiect.iam.gserviceaccount.com) — vei avea nevoie de ea la pasul 5.',
        ],
      },
      {
        title: 'Generează cheia JSON',
        details: [
          'Deschide Service Account-ul creat → tab-ul „Keys".',
          '„Add Key" → „Create new key" → alege formatul „JSON" → „Create".',
          'Se descarcă automat un fișier .json. Deschide-l cu un editor de text și copiază TOT conținutul.',
          'Lipește conținutul în câmpul „Service Account JSON" din formular.',
        ],
      },
      {
        title: 'Creează Drive-ul comun și adaugă Service Account-ul ca membru',
        details: [
          'În Google Drive (drive.google.com), în meniul din stânga → „Drive-uri comune" („Shared drives") → „Creare Drive comun" → dă-i un nume (ex. „Firmio").',
          'Deschide-l → meniul din dreptul numelui → „Gestionare membri" → adaugă adresa de email a Service Account-ului (de la pasul 3) cu rolul „Administrator de conținut" („Content manager").',
          'Rolul contează: cu „Cititor" sau „Comentator" aplicația poate citi, dar nu poate încărca fișiere.',
          'Dacă nu vezi opțiunea „Drive-uri comune", contul tău Google nu e pe un plan Workspace care le include — cere-i administratorului să activeze funcția.',
        ],
      },
      {
        title: 'Copiază ID-ul Drive-ului comun',
        details: [
          'Deschide Drive-ul comun în browser. URL-ul arată așa: drive.google.com/drive/folders/0AbCdEfGhIjKlMnOp…',
          'ID-ul este șirul de după „/folders/" — copiază-l în câmpul „ID Drive comun (rădăcină)".',
          'Pentru un Drive comun, ID-ul drive-ului este chiar ID-ul folderului său rădăcină, deci acest singur șir e suficient.',
        ],
      },
      {
        title: 'Testează și salvează',
        details: [
          'Apasă „Testează conexiunea" — aplicația chiar citește folderul, scrie un fișier de probă și îl șterge.',
          'Un rezultat corect arată așa: „folder „Firmio" (Shared Drive) · citire OK · scriere OK · ștergere OK".',
          'Dacă e OK, apasă „Salvează".',
        ],
      },
    ],
    notes: [
      'Fișierul JSON conține o cheie privată — nu îl trimite pe email și nu îl urca în repository-uri.',
      'Folosește un Service Account dedicat, membru doar în acest Drive comun: aplicația cere acces complet la Drive-urile la care contul e adăugat.',
      'Aplicația își organizează singură fișierele în foldere („Echipă", „Furnizori", „Clienți", „Proiecte"…). Poți redenumi sau muta folderele direct din Drive — aplicația le regăsește oricum, pentru că nu se orientează după nume.',
      'Fișierele create de aplicație aparțin Drive-ului comun, deci rămân accesibile echipei chiar dacă cineva pleacă din firmă.',
      'Dacă testul spune că folderul „nu este partajat", verifică pasul 5. Dacă spune că e în „My Drive", ai pus un folder personal în loc de un Drive comun.',
    ],
    links: [
      { label: 'Google Cloud Console', url: 'https://console.cloud.google.com' },
      {
        label: 'Documentație Service Accounts',
        url: 'https://cloud.google.com/iam/docs/service-account-overview',
      },
    ],
  },

  googleCalendar: {
    intro:
      'Sincronizarea calendarului folosește OAuth: aplicația primește permisiunea de a citi calendarul contului tău Google. Ai nevoie de: OAuth Client ID + Client Secret (create în Google Cloud) și un Refresh Token (obținut printr-o autorizare unică).',
    steps: [
      {
        title: 'Creează / selectează proiectul Google Cloud',
        details: [
          'Intră pe console.cloud.google.com. Poți refolosi proiectul creat pentru Google Drive.',
        ],
      },
      {
        title: 'Activează Google Calendar API',
        details: [
          'Meniu → „APIs & Services" → „Library" → caută „Google Calendar API" → „Enable".',
        ],
      },
      {
        title: 'Configurează ecranul de consimțământ (OAuth consent screen)',
        details: [
          '„APIs & Services" → „OAuth consent screen".',
          'Tip: „External" (dacă folosești Gmail personal) sau „Internal" (dacă ai Google Workspace).',
          'Completează numele aplicației și email-ul de contact → „Save".',
          'La „Test users" adaugă adresa de email al cărei calendar va fi citit (obligatoriu cât timp aplicația e în modul „Testing").',
        ],
      },
      {
        title: 'Creează OAuth Client ID + Secret',
        details: [
          '„APIs & Services" → „Credentials" → „Create Credentials" → „OAuth client ID".',
          'Application type: „Web application".',
          'La „Authorized redirect URIs" adaugă: https://developers.google.com/oauthplayground (o vom folosi la pasul următor).',
          'Apasă „Create" și copiază Client ID și Client Secret în formular.',
        ],
      },
      {
        title: 'Obține Refresh Token-ul (OAuth Playground)',
        details: [
          'Deschide developers.google.com/oauthplayground.',
          'Apasă rotița din dreapta-sus → bifează „Use your own OAuth credentials" → lipește Client ID și Client Secret.',
          'În lista din stânga (Step 1), scrie/alege scope-ul: https://www.googleapis.com/auth/calendar.readonly (sau …/auth/calendar pentru acces complet).',
          'Apasă „Authorize APIs" → alege contul → acceptă permisiunile (la avertismentul „unverified app" apasă „Advanced" → „Go to…").',
          'La Step 2 apasă „Exchange authorization code for tokens".',
          'Copiază valoarea „Refresh token" în câmpul din formular.',
        ],
      },
      {
        title: 'Testează și salvează',
        details: [
          'Redirect URI poate rămâne gol sau https://developers.google.com/oauthplayground.',
          'Apasă „Testează conexiunea" — ar trebui să vezi numărul de calendare disponibile → „Salvează".',
        ],
      },
    ],
    notes: [
      'Refresh Token-ul se afișează doar la prima autorizare. Dacă l-ai pierdut, revocă accesul din myaccount.google.com/permissions și reia pasul 5.',
      'Cât timp aplicația OAuth e în modul „Testing", token-urile pot expira după 7 zile — pentru utilizare permanentă publică aplicația („Publish app") din OAuth consent screen.',
    ],
    links: [
      {
        label: 'Google Cloud Console — Credentials',
        url: 'https://console.cloud.google.com/apis/credentials',
      },
      { label: 'OAuth 2.0 Playground', url: 'https://developers.google.com/oauthplayground' },
    ],
  },

  gmail: {
    intro:
      'Gmail Read-Only permite aplicației să citească inbox-ul (doar citire — nu trimite și nu șterge nimic) pentru Daily Email Summary. Refolosește OAuth Client-ul de la Google Calendar; singura diferență este scope-ul (gmail.readonly) și deci un Refresh Token separat.',
    steps: [
      {
        title: 'Pregătește OAuth Client-ul',
        details: [
          'Dacă ai configurat deja Google Calendar, poți lăsa Client ID și Client Secret goale — se refolosesc automat cele existente.',
          'Dacă nu, urmează mai întâi pașii 1–4 din ghidul Google Calendar (proiect, consent screen, OAuth Client ID).',
        ],
      },
      {
        title: 'Activează Gmail API',
        details: [
          'În console.cloud.google.com → „APIs & Services" → „Library" → caută „Gmail API" → „Enable".',
        ],
      },
      {
        title: 'Obține Refresh Token-ul cu scope-ul gmail.readonly',
        details: [
          'Deschide developers.google.com/oauthplayground.',
          'Rotița din dreapta-sus → „Use your own OAuth credentials" → lipește Client ID și Client Secret.',
          'La Step 1, introdu scope-ul: https://www.googleapis.com/auth/gmail.readonly',
          'Apasă „Authorize APIs" → autentifică-te cu contul al cărui inbox va fi citit (ex. contul CEO).',
          'La Step 2 apasă „Exchange authorization code for tokens" și copiază „Refresh token".',
        ],
      },
      {
        title: 'Completează formularul',
        details: [
          'Refresh Token (gmail.readonly): token-ul obținut la pasul 3.',
          'Adresa mailbox (CEO): adresa contului autorizat (ex. ceo@firma-ta.ro) — folosită la afișare.',
        ],
      },
      {
        title: 'Testează și salvează',
        details: [
          'Apasă „Testează conexiunea" — mesajul de succes confirmă adresa conectată → „Salvează".',
        ],
      },
    ],
    notes: [
      'Scope-ul gmail.readonly garantează accesul doar-citire: aplicația nu poate trimite, modifica sau șterge email-uri.',
      'Refresh Token-ul de Calendar NU funcționează pentru Gmail (scope diferit) — ai nevoie de o autorizare separată.',
      'Poți revoca oricând accesul din myaccount.google.com/permissions.',
    ],
    links: [
      { label: 'OAuth 2.0 Playground', url: 'https://developers.google.com/oauthplayground' },
      { label: 'Permisiunile contului Google', url: 'https://myaccount.google.com/permissions' },
    ],
  },

  smartbill: {
    intro:
      'SmartBill sincronizează automat facturile emise în aplicație. Ai nevoie de: email-ul contului SmartBill, token-ul API și CIF-ul firmei. Token-ul se obține din contul SmartBill Cloud — accesul la API este disponibil pe planurile care includ integrări (ex. Platinum).',
    steps: [
      {
        title: 'Autentifică-te în SmartBill Cloud',
        details: ['Intră pe cloud.smartbill.ro și autentifică-te cu contul firmei.'],
      },
      {
        title: 'Găsește token-ul API',
        details: [
          'Click pe numele contului / iconița din dreapta-sus → „Contul meu".',
          'Deschide secțiunea „Integrări" (sau „API").',
          'Acolo găsești „Token"-ul API — un șir lung de caractere. Copiază-l.',
          'Dacă nu vezi secțiunea, planul tău SmartBill nu include acces API — contactează SmartBill pentru upgrade.',
        ],
      },
      {
        title: 'Completează formularul',
        details: [
          'Username (email cont): adresa de email cu care te loghezi în SmartBill.',
          'Token / API Key: token-ul copiat la pasul 2.',
          'CIF firmă: codul fiscal exact cum apare în SmartBill la datele firmei (cu prefixul „RO" dacă firma e plătitoare de TVA, ex. RO47847495; fără „RO" dacă nu e).',
        ],
      },
      {
        title: 'Testează și salvează',
        details: [
          'Apasă „Testează conexiunea" — aplicația interoghează seriile de facturi pentru CIF-ul dat.',
          'Dacă primești eroare de autentificare, verifică perechea email + token; dacă primești eroare legată de CIF, verifică prefixul „RO".',
          'Apasă „Salvează" — sincronizarea facturilor va rula automat.',
        ],
      },
    ],
    notes: [
      'Într-un cont SmartBill cu mai multe firme, token-ul este același, dar CIF-ul selectează firma — configurează integrarea separat pentru fiecare firmă din aplicație.',
      'Token-ul oferă acces la datele de facturare — păstrează-l confidențial.',
    ],
    links: [
      { label: 'SmartBill Cloud', url: 'https://cloud.smartbill.ro' },
      { label: 'Documentație API SmartBill', url: 'https://api.smartbill.ro' },
    ],
  },
  livekit: {
    intro:
      'LiveKit este serverul video folosit de Firmio Meet și de canalele de voce din chat. Ai nevoie de un proiect LiveKit Cloud: adresa lui (URL), un API Key și API Secret-ul pereche. Audio-ul și video-ul merg direct între browser și LiveKit, nu prin serverul Firmio.',
    steps: [
      {
        title: 'Creează proiectul',
        details: [
          'Intră pe cloud.livekit.io și autentifică-te (sau fă-ți cont).',
          'Creează un proiect nou, de exemplu „Firmio". Dacă ți se cere o regiune, alege una din UE — sunt convorbirile interne ale firmei.',
        ],
      },
      {
        title: 'Creează o cheie API',
        details: [
          'În proiect, deschide Settings → Keys și apasă „Create key".',
          'Copiază API Key și API Secret. Copiază secretul imediat și păstrează-l în siguranță.',
          'Copiază și WebSocket URL-ul proiectului, de forma wss://proiect-xxxx.livekit.cloud.',
        ],
      },
      {
        title: 'Completează formularul',
        details: [
          'URL: WebSocket URL-ul (începe cu wss://).',
          'API Key și API Secret: perechea creată la pasul 2.',
        ],
      },
      {
        title: 'Testează și salvează',
        details: [
          'Apasă „Testează conexiunea" — aplicația cere lista camerelor active din proiect.',
          'Dacă primești „API key sau secret greșit", verifică perechea; dacă primești timeout, verifică URL-ul.',
          'Apasă „Salvează" — Firmio Meet și canalele de voce merg imediat, fără restart.',
        ],
      },
    ],
    notes: [
      'Planul gratuit LiveKit are o limită lunară de minute; verifică din când în când consumul în dashboard.',
      'Secretul dă acces la toate camerele proiectului — păstrează-l confidențial.',
    ],
    links: [
      { label: 'LiveKit Cloud', url: 'https://cloud.livekit.io' },
      { label: 'Documentație LiveKit', url: 'https://docs.livekit.io' },
    ],
  },
};

export function IntegrationHelpModal({
  integrationKey,
  label,
  onClose,
}: {
  integrationKey: string;
  label: string;
  onClose: () => void;
}) {
  const help = INTEGRATION_HELP[integrationKey];

  // Close on Escape.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!help) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Ghid configurare ${label}`}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-lg border border-gray-200 bg-card shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <span className="rounded-md bg-brand-50 p-1.5 text-brand-600">
              <Info className="h-4 w-4" />
            </span>
            <h2 className="text-base font-semibold text-gray-900">Cum configurezi {label}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Închide"
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto px-5 py-4">
          <p className="text-sm text-gray-600">{help.intro}</p>

          <ol className="mt-4 space-y-4">
            {help.steps.map((step, i) => (
              <li key={i} className="flex gap-3">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-600 text-xs font-semibold text-white">
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">{step.title}</p>
                  <ul className="mt-1 list-disc space-y-1 pl-4">
                    {step.details.map((d, j) => (
                      <li key={j} className="text-sm text-gray-600">
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            ))}
          </ol>

          {help.notes && help.notes.length > 0 && (
            <div className="mt-5 rounded-md border border-amber-500/30 bg-amber-500/10 p-3">
              <p className="flex items-center gap-1.5 text-sm font-semibold text-amber-700">
                <Lightbulb className="h-4 w-4" /> Bine de știut
              </p>
              <ul className="mt-1.5 list-disc space-y-1 pl-5">
                {help.notes.map((n, i) => (
                  <li key={i} className="text-sm text-amber-700/90">
                    {n}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {help.links && help.links.length > 0 && (
            <div className="mt-5">
              <p className="text-sm font-semibold text-gray-900">Linkuri utile</p>
              <ul className="mt-1.5 space-y-1">
                {help.links.map((l) => (
                  <li key={l.url}>
                    <a
                      href={l.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-brand-600 hover:underline"
                    >
                      {l.label}
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end border-t border-gray-200 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Am înțeles
          </button>
        </div>
      </div>
    </div>
  );
}
